import { useCallback } from 'react';

import { useBookCta } from '~/modules/enquiry/hooks/use-page-ctas';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import type { TeamMember } from '~/ui-rocco/pages/detail-shared';

export interface TeamMemberBooking {
    /** Set when the team's cards offer Book a meeting; undefined hides it. */
    onBookMeeting?: (member: TeamMember) => void;
}

/**
 * Book a meeting on the profile's team cards.
 *
 * Each card follows its member: only members with booking on show it, and one
 * with a personal form books through that form. The whole team stands down
 * when the page's own Book button is hidden — switched off in Forms, or not
 * for this visitor's audience — so a card never offers what the page has
 * turned off. Cards need the booking gate to name a person, so with the gate
 * off they offer nothing and the page's buttons take the meeting request.
 */
export const useTeamMemberBooking = (
    members: readonly TeamMember[],
): TeamMemberBooking => {
    const { ctas } = usePageCtas();
    const book = useBookCta();
    const { openFor } = book;

    // The section renders the roster, so it can tell the modal whether a
    // picker is coming before the fetch answers. One bookable member still
    // gets a field, because "anyone" is the other option in it. A member's
    // own form books only them, so it has none.
    const hasBookableMember = members.some((member) => member.bookingEnabled);
    const offered = book.visible && ctas.book.viaGate;

    const onBookMeeting = useCallback(
        (member: TeamMember) => {
            if (!member.bookingEnabled) return;
            openFor({
                preferredTeamMemberId: member.id,
                // The card already has both, so the pane opens on this person
                // rather than resolving its way to them.
                preferredTeamMemberName: member.name,
                paneImage: member.image || undefined,
                // Their own form books only them, without the page form's
                // questions.
                bookingFormId: member.bookingFormId ?? undefined,
                // Cards carry no sessions: the gate books the member's own
                // from the roster it loads, rather than the practice's.
                sessionIds: [],
                expectsTeamPicker: hasBookableMember && !member.bookingFormId,
            });
        },
        [openFor, hasBookableMember],
    );

    return { onBookMeeting: offered ? onBookMeeting : undefined };
};
